if (!customElements.get('bf-product-installation')) {
  customElements.define('bf-product-installation', class extends HTMLElement {
    connectedCallback() {
      this.cleanup();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.videos = [...this.querySelectorAll('[data-bf-installation-video]')];
      this.visible = new Set();
      this.activeVideo = null;
      this.suspended = false;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.pointer = matchMedia('(hover: hover) and (pointer: fine)');

      this.videos.forEach(video => {
        const media = video.closest('[data-bf-installation-media]');
        const button = media.querySelector('[data-bf-installation-activation]');
        media.addEventListener('pointerenter', () => {
          if (this.pointer.matches && !this.motion.matches) this.activate(video);
        }, { signal });
        button?.addEventListener('focus', () => {
          if (button.matches(':focus-visible') && !this.motion.matches) this.activate(video);
        }, { signal });
        button?.addEventListener('click', () => {
          if (this.activeVideo === video && !video.paused) this.pauseAll();
          else this.activate(video);
        }, { signal });
        video.addEventListener('playing', () => {
          if (this.activeVideo !== video || document.hidden || !this.visible.has(video)) {
            video.pause();
            return;
          }
          media.classList.add('is-video-ready');
          button?.setAttribute('aria-pressed', 'true');
        }, { signal });
        video.addEventListener('pause', () => button?.setAttribute('aria-pressed', 'false'), { signal });
        video.addEventListener('error', () => {
          media.classList.remove('is-video-ready');
          if (this.activeVideo === video) this.pauseAll();
        }, { signal });
      });

      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.intersectionRatio >= .3) this.visible.add(entry.target);
          else this.visible.delete(entry.target);
        });
        if (this.activeVideo && !this.visible.has(this.activeVideo)) this.pauseAll(false);
        if (!this.visible.size) {
          this.suspended = false;
          return;
        }
        if (!this.activeVideo && !this.suspended && !document.hidden && !this.motion.matches) {
          this.activate(this.videos.find(video => this.visible.has(video)));
        }
      }, { threshold: [0, .3] });
      this.videos.forEach(video => this.observer.observe(video));
      this.warmObserver = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.videos.forEach(video => this.prepare(video, this.motion.matches ? 'metadata' : 'auto'));
        this.warmObserver.disconnect();
      }, { rootMargin: '300px 0px' });
      this.warmObserver.observe(this);
      this.motion.addEventListener('change', () => this.pauseAll(), { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pauseAll();
      }, { signal });
      window.addEventListener('bf:media-play', event => {
        if (event.detail?.owner !== this) this.pauseAll();
      }, { signal });
    }

    prepare(video, preload = 'auto') {
      video.preload = preload;
      if (video.dataset.loaded) return;
      video.querySelectorAll('source[data-src]').forEach(source => { source.src = source.dataset.src; });
      video.dataset.loaded = 'true';
      video.load();
    }

    activate(video) {
      if (!video || document.hidden || !this.visible.has(video)) return;
      if (this.activeVideo === video && !video.paused) return;
      this.pauseAll(false);
      this.activeVideo = video;
      const request = this.playRequest;
      window.dispatchEvent(new CustomEvent('bf:media-play', { detail: { owner: this } }));
      video.muted = true;
      this.prepare(video);
      video.play().catch(() => {
        if (this.activeVideo === video && this.playRequest === request) this.pauseAll();
      });
    }

    pauseAll(suspended = true) {
      this.playRequest = (this.playRequest || 0) + 1;
      this.activeVideo = null;
      this.suspended = suspended;
      this.videos?.forEach(video => {
        video.pause();
        const media = video.closest('[data-bf-installation-media]');
        media.querySelector('[data-bf-installation-activation]')?.setAttribute('aria-pressed', 'false');
      });
    }

    disconnectedCallback() { this.cleanup(); }
    cleanup() {
      this.observer?.disconnect();
      this.warmObserver?.disconnect();
      this.controller?.abort();
      this.pauseAll();
    }
  });
}
